import type { BillingAccount, BillingInfo, Countries, Customer } from '@ctct/domain-layer';
import { formValueSessionStorage, myAccountApi } from '@ctct/infrastructure-layer';

import type { BillingInfoFormValues } from '../types';

export class FormDefaultValuesService {
  private readonly DEFAULT_COUNTRY_CODE = 'US';

  private defaultValues: BillingInfoFormValues;

  constructor(
    private billingInfo: BillingInfo,
    private countries: Countries,
    private customerInfo: Customer,
    private skipFormPrefill = false,
    private billingAccount?: BillingAccount
  ) {
    this.defaultValues = this.createDefaultValues();
  }

  public api() {
    return {
      getDefaultValues: () => this.getDefaultValues(),
    };
  }

  public data() {
    return {};
  }

  public handlers() {
    return {};
  }

  public getDefaultValues = () => this.defaultValues;

  private createDefaultValues(): BillingInfoFormValues {
    const customerId = this.billingAccount?.getCustomerId?.();

    const savedValues = formValueSessionStorage.loadPayflowBillingFormData(
      customerId
    ) as unknown as null | BillingInfoFormValues;

    if (savedValues) {
      return savedValues;
    }

    // If skipFormPrefill is true, return empty values instead of using billing info data
    if (this.skipFormPrefill) {
      return {
        firstname: '',
        lastname: '',
        companyname: '',
        address1: '',
        address2: '',
        city: '',
        zipcode: '',
        phone: '',
        state: '',
        country: this.getDefaultCountryCode(),
        email: this.getDefaultEmail(),
        currentTaxAgreement: undefined,
        vatNumber: '',
      };
    }

    const { lastname, phone, companyname } = myAccountApi.parseHiddenFields({
      lastname: this.billingInfo.getLastName(),
      phone: this.billingInfo.getPhone(),
      companyname: this.billingInfo.getCompanyName(),
    });

    // Get customer address to use as a fallback / initial value
    const customerAddress = this.customerInfo.getCustomerAddress();

    return {
      firstname: this.billingInfo.getFirstName(),
      lastname,
      companyname,
      address1: this.billingInfo.getAddress1() || customerAddress?.address1,
      address2: this.billingInfo.getAddress2() || customerAddress?.address2,
      city: this.billingInfo.getCity() || customerAddress?.city,
      zipcode: this.billingInfo.getPostalCode() || customerAddress?.zipcode,
      phone,
      state: this.billingInfo.getState(),
      country: this.getDefaultCountryCode(),
      email: this.getDefaultEmail(),
      currentTaxAgreement: this.billingInfo.getTaxExemptionAgreement(),
      vatNumber: this.billingInfo.getNonUsTaxExemptionNumber(),
    };
  }

  private getDefaultCountryCode() {
    const accountCountry = this.billingInfo.getCountry();
    const customerCountry = this.customerInfo.getPhysicalAddressCountryCode();
    const fallbackCountry = this.customerInfo.getBillingInfoCountryFallback();
    const firstCountryFromList = this.countries.getCountries()[0]?.code;

    return accountCountry || customerCountry || fallbackCountry || firstCountryFromList || this.DEFAULT_COUNTRY_CODE;
  }

  private getDefaultEmail() {
    const accountEmail = this.billingInfo.getEmail();
    const [firstEmailFromList] = this.billingInfo.getAllEmails();

    return accountEmail || firstEmailFromList || '';
  }
}
